import * as Sentry from '@sentry/vue'

import type {
  TripTypesSideBar,
  TripTypesSideBarSection,
  TripTypesSideBarContentCategoryTrip,
  TripTypesSideBarCategoryTripsResponse,
  TopNavBarItem,
  TripTypesSideBarResponse,
} from '@layers/web/services/types/navigation-api.ts'
import { CACHE_KEYS } from '@layers/web/constants/cache.ts'

export class NavigationApiService {
  static async fetchTripTypesSidebar (): Promise<TripTypesSideBar> {
    try {
      const { urls: localeURLs } = useLocaleStore()
      const { market } = useRuntimeConfig().public

      return await serverCacheStaleWhileRevalidate(
        CACHE_KEYS.SIDEBAR,
        async () => {
          const { data: res } = await apiFetch<TripTypesSideBarResponse>(
            `/${market}/sidebar?trips=0`,
            { retryDelay: 5000 }
          )

          if (!Object.keys(res || {}).length) {
            throw new Error('Empty sidebar response')
          }

          const sections: TripTypesSideBarSection[] = Object.values(res.content || {}).map((section) => ({
            name: section.name,
            categories: Object.values(section.categories).map((category) => ({
              id: String(category.category_id),
              name: category.name,
              url: category.category_links[0].url,
              trips: [],
            })),
          }))

          // Insert demanding-trips section with Explore More category in third position
          if (['sv', 'no'].includes(market)) {
            sections.splice(2, 0, {
              name: 'demanding-trips',
              categories: [
                {
                  id: 'explore-more',
                  name: 'Explore More',
                  url: `${localeURLs.campaign}/explore-more`,
                  trips: [],
                },
              ],
            })
          }

          const sidebar: TripTypesSideBar = {
            toplinks: res.toplinks || [],
            sections,
          }

          return sidebar
        },
        { retryDelayMs: 5000 }
      )
    } catch (error: any) {
      Sentry.setContext('details', error.data)
      Sentry.captureException(new Error('Failed to fetch trip types sidebar'), {
        level: 'error',
        tags: {
          operation: 'fetchTripTypesSidebar',
          service: 'navigation-api',
        },
      })

      throw error
    }
  }

  static async fetchTripsForSidebarCategory (
    categoryId: string
  ): Promise<TripTypesSideBarContentCategoryTrip[]> {
    const { market } = useRuntimeConfig().public

    try {
      const { data: res } = await apiFetch<TripTypesSideBarCategoryTripsResponse>(
        `/${market}/sidebar/category/${categoryId}/trips`
      )

      return (res?.trips || []).map((trip) => ({
        name: trip.tripname,
        url: trip.url,
        nameTranslationKey: trip.tripname_translationkey,
        urlTranslationKey: trip.url_translationkey,
      }))
    } catch (error: any) {
      Sentry.setContext('details', error.data)
      Sentry.captureException(new Error('Failed to fetch trips for sidebar category'), {
        level: 'error',
        tags: {
          operation: 'fetchTripsForSidebarCategory',
          service: 'navigation-api',
          categoryId,
        },
      })

      throw error
    }
  }

  static async fetchTopNavbar (hasAgentCookie: boolean): Promise<TopNavBarItem[]> {
    const { market } = useRuntimeConfig().public

    try {
      return await serverCacheStaleWhileRevalidate(
        'topnavbar',
        async () => {
          const { data: res } = await apiFetch<TopNavBarItem[]>(
            `/${market}/topnavbar`,
            { retryDelay: 5000 }
          )

          if (!res.length) {
            throw new Error('Empty topnavbar response')
          }

          return res
        },
        {
          ignoreCache: hasAgentCookie,
          retryDelayMs: 5000,
        }
      )
    } catch (error: any) {
      Sentry.setContext('details', error.data)
      Sentry.captureException(new Error('Failed to fetch top navbar'), {
        level: 'error',
        tags: {
          operation: 'fetchTopNavbar',
          service: 'navigation-api',
          market,
          hasAgentCookie,
        },
      })

      throw error
    }
  }

  static async fetchStartCategories (): Promise<string[]> {
    try {
      const { market } = useRuntimeConfig().public
      const { data: res } = await apiFetch<string[]>(`/${market}/categories-start`)
      return res
    } catch (error: any) {
      Sentry.setContext('details', error.data)
      Sentry.captureException(new Error('Failed to fetch start categories'), {
        level: 'error',
        tags: {
          operation: 'fetchStartCategories',
          service: 'navigation-api',
        },
      })

      throw error
    }
  }
}
